import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import { format } from 'date-fns';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as BalanceIcon,
} from '@mui/icons-material';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Dashboard() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    balance: 0,
    recentTransactions: [],
  });
  const [spendingData, setSpendingData] = useState({
    labels: [],
    datasets: [],
  });
  const [categoryData, setCategoryData] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [spendingResponse, transactionsResponse] = await Promise.all([
        axios.get('https://stu148664.webhosting.arden.ac.uk/COM6008/api/analysis/spending-patterns'),
        axios.get('https://stu148664.webhosting.arden.ac.uk/COM6008/api/transactions'),
      ]);

      // Process spending data
      const spendingData = spendingResponse.data;
      setSpendingData({
        labels: spendingData.category_distribution.map(item => item.category),
        datasets: [
          {
            label: 'Spending by Category',
            data: spendingData.category_distribution.map(item => item.amount),
            backgroundColor: [
              theme.palette.primary.main,
              theme.palette.secondary.main,
              theme.palette.success.main,
              theme.palette.error.main,
              theme.palette.warning.main,
            ],
          },
        ],
      });

      // Process category data
      const transactions = transactionsResponse.data;
      const monthlyData = processMonthlyData(transactions);
      setCategoryData({
        labels: monthlyData.labels,
        datasets: [
          {
            label: 'Income',
            data: monthlyData.income,
            borderColor: theme.palette.success.main,
            backgroundColor: theme.palette.success.light,
            tension: 0.4,
            fill: true,
          },
          {
            label: 'Expenses',
            data: monthlyData.expenses,
            borderColor: theme.palette.error.main,
            backgroundColor: theme.palette.error.light,
            tension: 0.4,
            fill: true,
          },
        ],
      });

      // Calculate summary
      const totalIncome = spendingData.summary.total_income;
      const totalExpenses = spendingData.summary.total_expenses;
      const balance = spendingData.summary.balance;

      setSummary({
        totalIncome,
        totalExpenses,
        balance,
        recentTransactions: transactions.slice(0, 5),
      });

      setLoading(false);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      setLoading(false);
    }
  };

  const processMonthlyData = (transactions) => {
    const months = {};
    transactions.forEach(transaction => {
      const month = format(new Date(transaction.transaction_date), 'MMM yyyy');
      if (!months[month]) {
        months[month] = { income: 0, expenses: 0 };
      }
      if (transaction.transaction_type === 'income') {
        months[month].income += transaction.amount;
      } else {
        months[month].expenses += transaction.amount;
      }
    });

    const sortedMonths = Object.keys(months).sort((a, b) => {
      return new Date(a) - new Date(b);
    });

    return {
      labels: sortedMonths,
      income: sortedMonths.map(month => months[month].income),
      expenses: sortedMonths.map(month => months[month].expenses),
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold', color: theme.palette.primary.main }}>
        Dashboard
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Total Income</Typography>
              </Box>
              <Typography variant="h4">
                ${summary.totalIncome.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingDownIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Total Expenses</Typography>
              </Box>
              <Typography variant="h4">
                ${summary.totalExpenses.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BalanceIcon sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Balance</Typography>
              </Box>
              <Typography variant="h4">
                ${summary.balance.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Monthly Overview
            </Typography>
            <Box sx={{ height: 400 }}>
              <Line
                data={categoryData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: theme.palette.divider,
                      },
                    },
                    x: {
                      grid: {
                        color: theme.palette.divider,
                      },
                    },
                  },
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Spending by Category
            </Typography>
            <Box sx={{ height: 400 }}>
              <Doughnut
                data={spendingData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Paper sx={{ p: 3, mt: 3, borderRadius: 2, boxShadow: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Recent Transactions
        </Typography>
        <Grid container spacing={2}>
          {summary.recentTransactions.map((transaction) => (
            <Grid item xs={12} key={transaction.id}>
              <Card variant="outlined" sx={{ 
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease-in-out',
                }
              }}>
                <CardContent>
                  <Grid container alignItems="center">
                    <Grid item xs>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {transaction.description}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {format(new Date(transaction.transaction_date), 'MMM dd, yyyy')}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography
                        variant="h6"
                        color={transaction.transaction_type === 'income' ? 'success.main' : 'error.main'}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {transaction.transaction_type === 'income' ? '+' : '-'}$
                        {transaction.amount.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
}

export default Dashboard; 